import React from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { useIntl } from 'react-intl';
import numeral from 'numeral';
import { currencySymbol } from '../../../../../utils/formatters';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from 'recharts';

const SuccessionSellerPerspective = ({ result, query, company }) => {
  const intl = useIntl();
  const sellerCashFlow = get(result, 'cashFlow.illustration.sellerCashFlow', []);
  const cashFlowAnalysisWord = get(company, 'cash_flow_analysis_word', 'Cash Flow Analysis');
  
  // Feature flags - using the same pattern as original components
  const features = get(company, 'company.data.features', []);
  const showCheckSwap = features.includes('check_swap');
  const showConsultingAgreements = features.includes('cash_flow_consulting_agreements');
  const showSellerNote = features.includes('cashflow_seller_note');
  const showOrdinaryTaxes = features.includes('cash_flow_ordinary_income_tax_rates');
  const showBranchExpenses = features.includes('cashflow_branch_expenses');
  const showLifetimeCapitalGains = features.includes('cashflow_lifetime_capital_gains_exemption');
  const showPurchasePrice = features.includes('cashflow_purchase_price');
  const cfaEducationBanner = features.includes('cfa_education_banner');
  const showBranchRetention = features.includes('cash_flow_branch_retention');
  const continuingPaymentsFINRA = features.includes('continuing_payments_FINRA_footnote');

  // Formatting
  const dollarFormat = '0,0.00';
  const percentFormat = '0%';
  const decimalPercentFormat = '0.0%';

  const formatCurrency = (value) => {
    const sym = currencySymbol();
    if (!value || isNaN(value)) return `${sym}0.00`;
    return `${sym}${numeral(value).format(dollarFormat)}`;
  };

  const formatPercentage = (value, useDecimal = false) => {
    if (!value || isNaN(value)) return '0%';
    return numeral(value).format(useDecimal ? decimalPercentFormat : percentFormat);
  };

  // Get tax rates
  const taxRate = get(sellerCashFlow, '[0].taxRate', 0.35); // Default to 35%
  const capitalGainsRate = get(result, 'cashFlow.cashflowSellerLifetimeCapitalGainsTax', 0.25); // Default to 25%
  const consultingTaxRate = taxRate; // Don't multiply by 100

  // Get consulting agreement data
  const consultingPercentOfDeal = get(query, 'consultingPercentOfDeal', 0);
  const purchasePrice = get(query, 'purchasePrice', 0);

  // Prepare chart data
  const chartData = sellerCashFlow.map((cf, index) => ({
    year: `${intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.year_label' })} ${index + 1}`,
    freeCashFlow: parseFloat(cf.freeCashFlow) || 0,
    afterTaxNetIncome: parseFloat(cf.afterTaxNetIncome) || 0,
    cumulativeProceeds: parseFloat(cf.cumulativeProceeds) || 0
  }));

  if (!sellerCashFlow || sellerCashFlow.length === 0) {
    return (
      <div className="bg-succession-bg-primary p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-succession-text-primary mb-4">
          {intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.table_header' })}
        </h3>
        <p className="text-succession-text-muted">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.no_data' })}</p>
      </div>
    );
  }

  return (
    <div className="bg-succession-bg-primary succession-seller-perspective">
      {/* Print-specific styles */}
      <style jsx>{`
        @media print {
          .recharts-wrapper, .recharts-responsive-container {
            display: none !important;
          }
          table {
            font-size: 8px !important;
          }
          th, td {
            padding: 2px 4px !important;
            font-size: 8px !important;
          }
        }
      `}</style>
      
      {/* Chart Section */}
      <div className="mb-8 chart-section">
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 80, bottom: 5 }}>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="var(--succession-border)"
              />
              <XAxis 
                dataKey="year"
                tick={{ fill: 'var(--succession-text-secondary)' }}
                stroke="var(--succession-border)"
              />
              <YAxis 
                tickFormatter={(value) => formatCurrency(value)}
                tick={{ fill: 'var(--succession-text-secondary)' }}
                stroke="var(--succession-border)"
              />
              <Tooltip
                formatter={(value) => [formatCurrency(value), intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.chart_free_cash_flow' })]}
                labelFormatter={(label) => label}
                contentStyle={{
                  backgroundColor: 'var(--succession-bg-primary)',
                  border: '1px solid var(--succession-border)',
                  borderRadius: '6px',
                  color: 'var(--succession-text-primary)'
                }}
                labelStyle={{ color: 'var(--succession-text-primary)' }}
                itemStyle={{ color: 'var(--succession-text-primary)' }}
              />
              <Legend
                wrapperStyle={{ color: 'var(--succession-text-primary)' }}
              />
              <Line
                type="monotone"
                dataKey="freeCashFlow"
                stroke="var(--succession-primary)"
                strokeWidth={2}
                name={intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.chart_free_cash_flow' })}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto printable-table">
        <table className="w-full border-collapse border border-succession-border succession-table">
          <thead>
            <tr>
              <th colSpan={sellerCashFlow.length + 1} className="bg-succession-bg-secondary px-4 py-3 text-left font-semibold text-succession-text-primary border">
                {intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.table_header' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Year header */}
            <tr>
              <td className="px-4 py-2 font-semibold text-succession-text-primary border">
                <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.year_label' })}</strong>
              </td>
              {sellerCashFlow.map((cf, i) => (
                <td key={i} className="px-4 py-2 text-right font-semibold text-succession-text-primary border">
                  <strong>{i + 1}</strong>
                </td>
              ))}
            </tr>

            {/* Production section depends on ordinary vs non-ordinary feature flag */}
            {showOrdinaryTaxes && (
              // Ordinary Structure (show production details)
              <>
                {/* Revenue Growth */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border italic">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_revenue_growth' })}</td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatPercentage(cf.revenueGrowth)}
                    </td>
                  ))}
                </tr>

                {/* Personal Production */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_personal_production' })}</td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatCurrency(cf.personalProduction)}
                    </td>
                  ))}
                </tr>

                {/* Payout Rate */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_payout_rate' }, { payoutWord: get(result, 'company.cashflow_payout_word', 'Payout') })}</td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatPercentage(cf.payoutRate, true)}
                    </td>
                  ))}
                </tr>

                {/* Payout */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_payout' }, { payoutWord: get(result, 'company.cashflow_payout_word', 'Payout') })}</td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatCurrency(cf.payout)}
                    </td>
                  ))}
                </tr>

                {/* Retirement Choice - RJA specific */}
                {typeof document !== 'undefined' && document.location.href.match('rja') && (
                  <tr>
                    <td className="px-4 py-2 font-semibold text-succession-text-primary border">
                      <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_retirement_choice' })}</strong>
                    </td>
                    <td className="px-4 py-2 border"></td>
                  </tr>
                )}
              </>
            )}

            {/* Payment structure depends on ordinary vs non-ordinary feature flag */}
            {showOrdinaryTaxes ? (
              // Ordinary Structure (detailed payment breakdown)
              <>
                {/* Book Sell Payment Received */}
                <tr>
                  <td className="px-4 py-2 font-semibold text-succession-text-primary border">
                    <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_book_sell_payment' })}</strong>
                  </td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right font-semibold text-succession-text-primary border">
                      <strong>{formatCurrency(cf.bookSalePaymentReceived)}</strong>
                    </td>
                  ))}
                </tr>

                {/* Lump Sum Payment Received */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_lump_sum_received' })}</td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatCurrency(cf.lumpSumPaymentReceived)}
                    </td>
                  ))}
                </tr>

                {/* Fixed Payment Received */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_fixed_payments_received' })}</td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatCurrency(cf.fixedPaymentReceived)}
                    </td>
                  ))}
                </tr>

                {/* Contingent Payment Received */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">
                    {typeof document !== 'undefined' && !document.location.href.match('icd') ?
                      intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_contingent_continuity' }) :
                      intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_contingent_participation' })
                    }
                  </td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatCurrency(cf.contingentPaymentReceived)}
                    </td>
                  ))}
                </tr>

                {/* Check Swap - CONDITIONAL based on feature flag */}
                {showCheckSwap && (
                  <>
                    <tr>
                      <td className="px-4 py-2 font-semibold text-succession-text-primary border">
                        <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_check_swap' })}</strong>
                      </td>
                      <td className="px-4 py-2 border"></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_transition_assistance_bonus' })}</td>
                      {sellerCashFlow.map((cf, i) => (
                        <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                          {formatCurrency(cf.transitionAssistanceLoanBonus || 0)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_check_swap_from_buyer' })}</td>
                      {sellerCashFlow.map((cf, i) => (
                        <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                          {formatCurrency(cf.checkSwapPayment || 0)}
                        </td>
                      ))}
                    </tr>
                  </>
                )}

                {/* Seller Note - only show if feature enabled */}
                {showSellerNote && (
                  <tr>
                    <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_seller_note' })}</td>
                    {sellerCashFlow.map((cf, i) => (
                      <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                        {formatCurrency(cf.sellerNotePayment)}
                      </td>
                    ))}
                  </tr>
                )}
              </>
            ) : (
              // Non-Ordinary Structure (simplified payment structure like the original)
              <>
                {/* Down Payment */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_down_payment' })}</td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatCurrency(cf.lumpSumPaymentReceived)}
                    </td>
                  ))}
                </tr>

                {/* Earn Out */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_earn_out' })}</td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatCurrency(cf.contingentPaymentReceived)}
                    </td>
                  ))}
                </tr>

                {/* Seller Carried Note Payments - only show if feature enabled */}
                {showSellerNote && (
                  <tr>
                    <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_seller_carried_note' })}</td>
                    {sellerCashFlow.map((cf, i) => (
                      <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                        {formatCurrency(cf.sellerNotePayment)}
                      </td>
                    ))}
                  </tr>
                )}

                {/* Consulting Agreement - only show if feature enabled */}
                {showConsultingAgreements && (
                  <tr>
                    <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_consulting_agreement' })}</td>
                    {sellerCashFlow.map((cf, i) => (
                      <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                        {formatCurrency(cf.consultingAgreement)}
                      </td>
                    ))}
                  </tr>
                )}
              </>
            )}

            {/* Bold HR above Pretax Net Income */}
            <tr>
              <td colSpan={sellerCashFlow.length + 1} className="border-b-4 border-succession-border p-0"></td>
            </tr>

            {/* Pretax Net Income - Bold */}
            <tr>
              <td className="px-4 py-2 font-bold text-succession-text-primary border bg-succession-bg-secondary">
                <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_pretax_net_income' })}</strong>
              </td>
              {sellerCashFlow.map((cf, i) => (
                <td key={i} className="px-4 py-2 text-right font-bold text-succession-text-primary border bg-succession-bg-secondary">
                  <strong>{formatCurrency(cf.pretaxNetIncome)}</strong>
                </td>
              ))}
            </tr>

            {/* TAX TREATMENTS Section */}
            <tr>
              <td className="px-4 py-2 font-semibold text-succession-text-primary border">
                <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_tax_treatments' })}</strong>
              </td>
              <td className="px-4 py-2 border"></td>
            </tr>

            {/* Tax structure based on ordinary vs non-ordinary feature flag */}
            {showOrdinaryTaxes ? (
              // Ordinary Tax Structure (simplified)
              <>
                {/* Income Tax Rate */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border italic">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_income_tax_rate' })}</td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatPercentage(taxRate)}
                    </td>
                  ))}
                </tr>

                {/* Income Tax */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_income_tax' })}</td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(-(cf.taxes || 0))}
                    </td>
                  ))}
                </tr>

                {/* Consulting Agreement - only show if feature enabled */}
                {showConsultingAgreements && (
                  <tr>
                    <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_consulting_rate' }, { rate: formatPercentage(consultingTaxRate) })}</td>
                    {sellerCashFlow.map((cf, i) => (
                      <td key={i} className="px-4 py-2 text-right text-succession-error border">
                        {formatCurrency(-(cf.consultingAgreementTaxes || 0))}
                      </td>
                    ))}
                  </tr>
                )}

                {/* Bold HR above Total Taxes */}
                <tr>
                  <td colSpan={sellerCashFlow.length + 1} className="border-b-4 border-succession-border p-0"></td>
                </tr>

                {/* Total Taxes */}
                <tr>
                  <td className="px-4 py-2 font-bold text-succession-text-primary border">
                    <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_total_taxes' })}</strong>
                  </td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right font-bold text-succession-error border">
                      <strong>{formatCurrency(-(cf.totalTaxes || 0))}</strong>
                    </td>
                  ))}
                </tr>
              </>
            ) : (
              // Non-Ordinary Tax Structure (detailed breakdown)
              <>
                {/* Income Tax Rate */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border italic">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_income_rate' }, { rate: formatPercentage(taxRate) })}</td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatPercentage(0)}
                    </td>
                  ))}
                </tr>

                {/* Down Payment Tax */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_down_payment_rate' }, { rate: formatPercentage(capitalGainsRate) })}</td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(-(cf.downpaymentTaxes || 0))}
                    </td>
                  ))}
                </tr>

                {/* Earn Out Tax */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_earn_out_rate' }, { rate: formatPercentage(taxRate) })}</td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(-(cf.earnOutTaxes || 0))}
                    </td>
                  ))}
                </tr>

                {/* Seller Note taxes - only show if feature enabled */}
                {showSellerNote && (
                  <>
                    <tr>
                      <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_seller_note_interest_rate' }, { rate: formatPercentage(taxRate) })}</td>
                      {sellerCashFlow.map((cf, i) => (
                        <td key={i} className="px-4 py-2 text-right text-succession-error border">
                          {formatCurrency(-(cf.sellerNoteInterestTaxes || 0))}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_seller_note_principal_rate' }, { rate: formatPercentage(capitalGainsRate) })}</td>
                      {sellerCashFlow.map((cf, i) => (
                        <td key={i} className="px-4 py-2 text-right text-succession-error border">
                          {formatCurrency(-(cf.sellerNotePrincipalTaxes || 0))}
                        </td>
                      ))}
                    </tr>
                  </>
                )}

                {/* Consulting Agreement - only show if feature enabled */}
                {showConsultingAgreements && (
                  <tr>
                    <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_consulting_rate' }, { rate: formatPercentage(consultingTaxRate) })}</td>
                    {sellerCashFlow.map((cf, i) => (
                      <td key={i} className="px-4 py-2 text-right text-succession-error border">
                        {formatCurrency(-(cf.consultingAgreementTaxes || 0))}
                      </td>
                    ))}
                  </tr>
                )}

                {/* Bold HR above Total Taxes */}
                <tr>
                  <td colSpan={sellerCashFlow.length + 1} className="border-b-4 border-succession-border p-0"></td>
                </tr>

                {/* Total Taxes */}
                <tr>
                  <td className="px-4 py-2 font-bold text-succession-text-primary border">
                    <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_total_taxes' })}</strong>
                  </td>
                  {sellerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right font-bold text-succession-error border">
                      <strong>{formatCurrency(-(cf.totalTaxes || 0))}</strong>
                    </td>
                  ))}
                </tr>
              </>
            )}

            {/* After Tax Net Income */}
            <tr>
              <td className="px-4 py-2 font-semibold text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_after_tax_net_income' })}</td>
              {sellerCashFlow.map((cf, i) => (
                <td key={i} className="px-4 py-2 text-right font-semibold text-succession-text-primary border">
                  {formatCurrency(cf.afterTaxNetIncome)}
                </td>
              ))}
            </tr>

            {/* Debt Service - Bold */}
            <tr>
              <td className="px-4 py-2 font-bold text-succession-text-primary border bg-succession-bg-secondary">
                <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_debt_service' })}</strong>
              </td>
              <td className="px-4 py-2 border bg-succession-bg-secondary"></td>
            </tr>

            {/* Transition Assistance Loan Repayment */}
            <tr>
              <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_transition_assistance_repayment' })}</td>
              {sellerCashFlow.map((cf, i) => (
                <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                  {formatCurrency(cf.transitionAssistanceLoanRepayment)}
                </td>
              ))}
            </tr>

            {/* Bold HR above Free Cash Flow */}
            <tr>
              <td colSpan={sellerCashFlow.length + 1} className="border-b-4 border-succession-border p-0"></td>
            </tr>

            {/* Free Cash Flow */}
            <tr>
              <td className="px-4 py-2 font-bold text-succession-text-primary border bg-succession-bg-secondary">
                <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_free_cash_flow' })}</strong>
              </td>
              {sellerCashFlow.map((cf, i) => (
                <td key={i} className="px-4 py-2 text-right font-bold text-succession-text-primary border bg-succession-bg-secondary">
                  <strong>{formatCurrency(cf.freeCashFlow)}</strong>
                </td>
              ))}
            </tr>

            {/* Cumulative Proceeds - Bold */}
            <tr>
              <td className="px-4 py-2 font-bold text-succession-text-primary border bg-succession-bg-secondary">
                <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_perspective.row_cumulative_proceeds' })}</strong>
              </td>
              {sellerCashFlow.map((cf, i) => (
                <td key={i} className="px-4 py-2 text-right font-bold text-succession-text-primary border bg-succession-bg-secondary">
                  <strong>{formatCurrency(cf.cumulativeProceeds)}</strong>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const mapStateToProps = (state, props) => {
  return {
    result: {...state.cfa.result},
    query: {...state.cfa.query},
    company: { ...get(state, 'company', {}), ...get(state, 'notes.company', {}),},
  };
};

export default connect(mapStateToProps)(SuccessionSellerPerspective); 